import React, { useEffect } from "react";
import Head from "next/head";
import { ThemeProvider } from "@mui/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Theme from "../src/components/ui/Theme";
import HeaderToolbar from "../src/components/layout/HeaderToolbar";
import SessionBar from "../src/components/sessions/SessionBar";
import Navigation from "../src/components/layout/Navigation";
import PlaceTabs from "../src/components/layout/PlaceTabs";
import ToolLibrary from "../src/components/tools/ToolLibrary";
import { Provider, useDispatch, useSelector } from "react-redux";
import { useRouter } from "next/router";
import store from "../src/store/index";
import { loadCurrentUser } from "../src/store/realm-app";
import RealmApolloProvider from "../src/providers/RealmApolloProvider";
import { ToneContextProvider } from "../src/store/tone-context";
import ReactGA from "react-ga";
import ErrorBoundary from "../src/components/error/ErrorBoundary";
import { AssistantProvider } from "../src/components/assistant/AssistantProvider";
import Assistant from "../src/components/assistant/Assistant";
import { assistantOn } from "../src/components/assistant/api";
import { extensionOn } from "../src/lib/extensions/registry";
import { PlatformProvider, AppGate, platform } from "../src/components/platform/react";
import SiteSettings from "../src/components/platform/SiteSettings";
import ExamplePreview from "../src/components/platform/ExamplePreview";
import ExtensionHost from "../src/components/extensions/ExtensionHost";
import { applyTuning } from "../src/lib/similarity/profiles";
import '../styles/App.css'
import layoutStyles from '../src/components/layout/community.module.css';

function AuthSessionLoader({ children }) {
  const dispatch = useDispatch(), router = useRouter();
  const currentUser = useSelector((state) => state.realm.currentUser);

  useEffect(() => {
    dispatch(loadCurrentUser());
  }, [dispatch]);
  // Accounts from before the age screen confirm their date of birth before going on. The
  // policies it links to stay readable.
  useEffect(() => {
    const open = ["/signup", "/terms", "/privacy", "/guidelines"].includes(router.pathname) || router.pathname.startsWith("/copyright");
    if (currentUser?.ageConfirmed === false && !open) {
      router.replace({ pathname: "/signup", query: { confirm: "1", returnTo: router.asPath } });
    }
  }, [currentUser, router]);

  return children;
}

// The similarity engine as people's judgments have tuned it (src/lib/similarity/tuning.js), so what's
// worked out in the browser (how alike an idea is) agrees with the server.
function SimilarityTuning() {
  useEffect(() => {
    fetch("/api/similarity/tuning").then((response) => response.ok ? response.json() : null).then((data) => { if (data?.tuning) applyTuning(data.tuning); }).catch(() => {});
  }, []);
  return null;
}

// Plugins hear where the visitor goes (src/platform: "route.changed").
function RouteEvents() {
  const router = useRouter();
  useEffect(() => {
    const changed = (url) => platform().emit("route.changed", { url });
    changed(router.asPath);
    router.events.on("routeChangeComplete", changed);
    return () => router.events.off("routeChangeComplete", changed);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

export default function MyApp(props) {
  const { Component, pageProps } = props;
  const router = useRouter();

  useEffect(() => {
    // Remove the server-side injected CSS.
    const jssStyles = document.querySelector("#jss-server-side");
    if (jssStyles) {
      jssStyles.parentElement.removeChild(jssStyles);
    }

    // No analytics on a device that didn't pass the age screen.
    if (!document.cookie.split("; ").includes("tc_age_block=1")) {
      ReactGA.initialize("UA-146220050-1", {
        titleCase: false,
      });
    }
  }, []);

  return (
    <ErrorBoundary>
      <Head>
        <title>TrueChops</title>
        <meta
          name="viewport"
          content="minimum-scale=1, initial-scale=1, width=device-width"
        />
      </Head>

      <Provider store={store}>
        <AuthSessionLoader>
          <RealmApolloProvider>
            <ToneContextProvider>
              <ThemeProvider theme={Theme}>
                <PlatformProvider>
                <AssistantProvider>
                <RouteEvents />
                <SimilarityTuning />
                <SiteSettings />
                {extensionOn("examples") && <ExamplePreview />}
                {extensionOn("marketplace") && <ExtensionHost />}
                <CssBaseline />
                {/* Pages shown inside other apps (the Telegram Mini App) set `bare` to fill the view without the site's menu. */}
                {Component.bare ? <AppGate pathname={router.pathname} query={router.query}><Component {...pageProps} /></AppGate> : <>
                  <HeaderToolbar />
                  <Navigation />
                  <div className={layoutStyles.appContent}><PlaceTabs /><AppGate pathname={router.pathname} query={router.query}><Component {...pageProps} /></AppGate></div>
                  <SessionBar />
                  <ToolLibrary />
                  {assistantOn() && platform().appEnabled("assistant") && <Assistant />}
                </>}
                </AssistantProvider>
                </PlatformProvider>
                {/* <Footer /> */}
              </ThemeProvider>
            </ToneContextProvider>
          </RealmApolloProvider>
        </AuthSessionLoader>
      </Provider>
    </ErrorBoundary>
  );
}
